import {
  MAP_LAYER_IDS,
  MAP_SOURCE_ID,
} from '@components/app/SearchLandingPage/SearchResults/Map/MapboxLayers'
import type { AlgoliaTrialHit } from '@modules/trials/search/const'
import type { Hit } from 'instantsearch.js'
import type { MapLayerMouseEvent } from 'mapbox-gl'
import type { MapGeoJSONFeature } from 'maplibre-gl'

export interface HandleMapClickArgs {
  clickedFeatureId: string | number | null
  event: MapLayerMouseEvent
  mapRef: React.RefObject<maplibregl.Map>
  setClickedFeatureId: React.Dispatch<
    React.SetStateAction<string | number | null>
  >
  setIsOverlayOpen: React.Dispatch<React.SetStateAction<boolean>>
  setTrialsToPreview: React.Dispatch<
    React.SetStateAction<Hit<AlgoliaTrialHit>[]>
  >
  trialLocations: Hit<AlgoliaTrialHit>[]
}

export function handleMapClick({
  clickedFeatureId,
  event,
  mapRef,
  setClickedFeatureId,
  setIsOverlayOpen,
  setTrialsToPreview,
  trialLocations,
}: HandleMapClickArgs) {
  if (!mapRef.current) {
    return
  }

  const mapInstance = mapRef.current

  // Query the map for features at the click location
  const features = mapInstance.queryRenderedFeatures(
    [event.point.x, event.point.y],
    {
      layers: [MAP_LAYER_IDS.CLUSTERS, MAP_LAYER_IDS.UNCLUSTERED_POINT],
    },
  )

  // If no features are found, return
  if (!features.length) {
    return
  }

  // Get the first feature from the query
  const clickedFeature = features[0]
  if (!clickedFeature) {
    return
  }

  // Clear the previous feature state if one was clicked
  if (clickedFeatureId !== null) {
    mapInstance.setFeatureState(
      { id: clickedFeatureId, source: MAP_SOURCE_ID },
      { clicked: false },
    )
  }

  const updateClickedFeatureState = (feature: MapGeoJSONFeature) => {
    if (feature.id !== undefined && feature.id !== null) {
      mapInstance.setFeatureState(
        { id: feature.id, source: MAP_SOURCE_ID },
        { clicked: true },
      )
      setClickedFeatureId(feature.id)
    }
  }

  if (
    clickedFeature.layer.id === MAP_LAYER_IDS.CLUSTERS &&
    clickedFeature.properties?.cluster_id !== null
  ) {
    updateClickedFeatureState(clickedFeature)
    setIsOverlayOpen(true)

    const clusterId = clickedFeature.properties.cluster_id
    const source = mapInstance.getSource(
      MAP_SOURCE_ID,
    ) as maplibregl.GeoJSONSource

    if (source && typeof source.getClusterLeaves === 'function') {
      source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
        if (err) {
          return
        }

        const objectIds = leaves?.map((leaf) => leaf.properties?.id)
        const trialsAtFeatureLocation = trialLocations.filter((hit) =>
          hit.objectID ? objectIds?.includes(hit.objectID) : false,
        )

        setTrialsToPreview(trialsAtFeatureLocation)
      })
    }
  } else if (clickedFeature.layer.id === MAP_LAYER_IDS.UNCLUSTERED_POINT) {
    updateClickedFeatureState(clickedFeature)
    setIsOverlayOpen(true)

    const id = clickedFeature.properties?.id

    const trialsAtFeatureLocation = trialLocations.filter((hit) =>
      hit.objectID ? hit.objectID === id : false,
    )

    setTrialsToPreview(trialsAtFeatureLocation)
  }
}
